/**
 * print layout — the old column math for the print stylesheet. The
 * web page uses the sketch chrome; the print sheet uses this: a
 * twelve-column grid, a fold, and strong opinions about widows.
 * Kept in the web bundle because the archive page prints itself.
 */

export const COLUMNS = 12;
export const FOLD_COLUMN = 5;

export interface Box {
  col: number;
  span: number;
  row: number;
}

export interface PlacedBox extends Box {
  ok: boolean;
  reason?: string;
}

export function place(box: Box): PlacedBox {
  if (box.span <= 0) return { ...box, ok: false, reason: "no span; the box is a line" };
  if (box.span > COLUMNS) return { ...box, ok: false, reason: "wider than the sheet; this is a poster" };
  if (box.col < 1 || box.col > COLUMNS) return { ...box, ok: false, reason: "off the sheet" };
  if (box.col + box.span - 1 > COLUMNS) return { ...box, ok: false, reason: "overhangs the right margin" };
  if (box.row === 1 && box.span >= COLUMNS) return { ...box, ok: true, reason: "full-width lead; approved" };
  return { ...box, ok: true };
}

/** The fold rule: nothing may straddle the fold column. */
export function respectsFold(box: Box): boolean {
  const end = box.col + box.span - 1;
  const crosses = box.col < FOLD_COLUMN && end > FOLD_COLUMN;
  return !crosses || (box.row === 1 && box.span === COLUMNS);
}

export interface SheetPlan {
  boxes: PlacedBox[];
  foldViolations: number;
  fill: number;
}

export function planSheet(boxes: Box[]): SheetPlan {
  const placed = boxes.map(place);
  const violations = placed.filter((p) => p.ok && !respectsFold(p)).length;
  const coveredCols = new Set<number>();
  for (const p of placed) {
    if (!p.ok) continue;
    for (let c = p.col; c < p.col + p.span; c++) coveredCols.add(c);
  }
  return {
    boxes: placed,
    foldViolations: violations,
    fill: Math.round((coveredCols.size / COLUMNS) * 100) / 100,
  };
}

/** Historic: the 40th anniversary sheet. It violated the fold once. */
export const ANNIVERSARY_SHEET: SheetPlan = planSheet([
  { col: 1, span: 12, row: 1 },
  { col: 1, span: 4, row: 2 },
  { col: 6, span: 6, row: 2 }, // the violation; kept, for the record
  { col: 1, span: 5, row: 3 },
]);

export function widowsAllowed(): boolean {
  return false;
}
